import { useId, useState } from 'react'

import { FxButton } from '@/components/fx'
import { exportsApi, exportErrorMessage, readBlobError } from '@/lib/api'
import type { ExportFormat, ExportOptions } from '@/lib/api'
import type { ArmRequest } from './armOptions'

/**
 * The export.
 *
 * Three decisions worth knowing about:
 *
 * 1. **The scope is one choice, not two switches.** `method` takes exactly
 *    one of `all`, `validated` and `specific`, so the three are radio
 *    buttons: picking a second one would silently drop the first.
 *    `validated` now really filters — it used to fall through to "all", which
 *    is why this screen offered only the id-list narrowing — and it means
 *    *at least one field reviewed*, not "fully reviewed", so the label says
 *    that. The value is never built from free input: anything the gateway
 *    does not know, an empty `method=` included, is a 400 `unknown_method`.
 * 2. **The file follows the reading on screen.** `?arm=` exports what that
 *    reading made of the same scans, and `&model=` picks between two models
 *    of one arm exactly as the table does, so the download matches what was
 *    looked at. With the delivered values on screen both are omitted.
 * 3. **Confidence columns are off by default**, as the gateway has them: a
 *    delivery is a list of values, and a percentage beside every one of them
 *    is noise to whoever receives the file. It is one checkbox away.
 */

/** What the person may narrow the file to. `geprueft` and `angezeigt` are
 *  the two `method` values that filter; `alle` is the gateway's own default. */
type ExportScope = 'alle' | 'geprueft' | 'angezeigt'

export interface RecordsExportProps {
  templateId: string
  /** The reading the table is showing, or null for the delivered values. */
  armRequest: ArmRequest | null
  /** The document ids currently on screen, for the narrowed export. Empty
   *  disables that option. */
  visibleDocumentIds: string[]
  /** True when a filter or the search box is narrowing the table, so the
   *  narrowed export is worth offering. */
  narrowed: boolean
}

export function RecordsExport({
  templateId,
  armRequest,
  visibleDocumentIds,
  narrowed,
}: RecordsExportProps) {
  const [includeConfidence, setIncludeConfidence] = useState(false)
  const [scope, setScope] = useState<ExportScope>('alle')
  const [busy, setBusy] = useState<ExportFormat | null>(null)
  const [message, setMessage] = useState<{ tone: 'info' | 'danger'; text: string } | null>(null)
  const confidenceId = useId()
  const scopeName = useId()

  const canNarrow = narrowed && visibleDocumentIds.length > 0
  // A scope whose precondition fell away (the search box was cleared while
  // "nur die angezeigten" was chosen) exports everything rather than an empty
  // id list, which the gateway would read as "no documents".
  const effectiveScope: ExportScope = scope === 'angezeigt' && !canNarrow ? 'alle' : scope

  function methodFor(value: ExportScope): Partial<ExportOptions> {
    if (value === 'geprueft') return { method: 'validated' as const }
    if (value === 'angezeigt') {
      return { method: 'specific' as const, documentIds: visibleDocumentIds }
    }
    return {}
  }

  async function run(format: ExportFormat) {
    setBusy(format)
    setMessage(null)
    try {
      const outcome = await exportsApi.download(templateId, format, {
        ...(armRequest
          ? { arm: armRequest.arm, ...(armRequest.model ? { model: armRequest.model } : {}) }
          : {}),
        ...methodFor(effectiveScope),
        includeConfidence,
      })
      if (outcome === 'newTab') {
        setMessage({
          tone: 'info',
          text: 'Die Datei wurde in einem neuen Tab geöffnet — von dort können Sie sie sichern.',
        })
      } else if (outcome === 'blocked') {
        setMessage({
          tone: 'danger',
          text: 'Der Browser hat das Fenster blockiert. Erlauben Sie Pop-ups für diese Seite.',
        })
      }
    } catch (error) {
      // A failed file request answers with a Blob, not with JSON, so the
      // gateway's reason has to be read back out of it first.
      setMessage({ tone: 'danger', text: exportErrorMessage(await readBlobError(error)) })
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="flex flex-col items-start gap-2 sm:items-end">
      <div className="flex flex-wrap gap-2">
        <FxButton
          variant="primary"
          icon="download"
          disabled={busy !== null}
          onClick={() => void run('xlsx')}
        >
          {busy === 'xlsx' ? 'Datei wird erstellt …' : 'Excel-Datei herunterladen'}
        </FxButton>
        <FxButton variant="outlined" disabled={busy !== null} onClick={() => void run('csv')}>
          {busy === 'csv' ? 'CSV wird erstellt …' : 'CSV'}
        </FxButton>
      </div>

      <details className="text-small">
        <summary className="cursor-pointer text-primary-text">Export-Optionen</summary>
        <div className="mt-2 flex flex-col gap-3">
          <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
            <legend className="p-0 text-small text-text-muted">Welche Datensätze</legend>
            <ScopeChoice
              name={scopeName}
              value="alle"
              current={effectiveScope}
              onChange={setScope}
              label="Alle ausgewerteten Datensätze"
            />
            <ScopeChoice
              name={scopeName}
              value="geprueft"
              current={effectiveScope}
              onChange={setScope}
              label="Nur geprüfte (mindestens ein Feld geprüft)"
            />
            <ScopeChoice
              name={scopeName}
              value="angezeigt"
              current={effectiveScope}
              onChange={setScope}
              disabled={!canNarrow}
              label={
                canNarrow
                  ? `Nur die ${visibleDocumentIds.length} angezeigten Datensätze`
                  : 'Nur die angezeigten Datensätze (kein Filter aktiv)'
              }
            />
          </fieldset>

          <label className="flex items-center gap-2" htmlFor={confidenceId}>
            <input
              id={confidenceId}
              type="checkbox"
              checked={includeConfidence}
              onChange={(event) => setIncludeConfidence(event.target.checked)}
            />
            Sicherheitswerte als zusätzliche Spalten
          </label>
        </div>
      </details>

      {message && (
        <p
          role={message.tone === 'danger' ? 'alert' : 'status'}
          className={`m-0 max-w-sm text-small ${
            message.tone === 'danger' ? 'text-danger-text' : 'text-text-muted'
          }`}
        >
          {message.text}
        </p>
      )}
    </div>
  )
}

/** One radio of the scope group — a real `<input type="radio">` so the group
 *  is one tab stop and the arrow keys move within it. */
function ScopeChoice({
  name,
  value,
  current,
  onChange,
  label,
  disabled,
}: {
  name: string
  value: ExportScope
  current: ExportScope
  onChange: (value: ExportScope) => void
  label: string
  disabled?: boolean
}) {
  const id = useId()
  return (
    <label className={`flex items-center gap-2 ${disabled ? 'text-text-muted' : ''}`} htmlFor={id}>
      <input
        id={id}
        type="radio"
        name={name}
        value={value}
        checked={current === value}
        disabled={disabled}
        onChange={() => onChange(value)}
      />
      {label}
    </label>
  )
}
